'use client';

import { c as _c } from "react/compiler-runtime";
import { useEffect, useEffectEvent, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { useDownloadEmVoo } from '@/shared/hooks/useDownloadEmVoo';
import { useToast } from '@/shared/providers';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { atualizarEmissaoDoCertificado, fetchNegociacaoTelefones, obterEmissaoAtualDoCertificado, solicitarPdfDoCertificado } from '../store/actions';
import { selectAcontecimentosDoCertificado, selectCertificadoNegociacaoBaixandoPdf, selectEstadoDoCertificadoNegociacao } from '../store/selectors';
import { reconsultaDoCertificadoPedida } from '../store/slice';
import type { CertificadoNegociacaoResumoDto, OpcoesDoCertificadoNegociacao } from '../types/certificadoNegociacaoTypes';
import { nomeDoArquivoDoCertificado, rotaDoCertificado } from '../utils/certificadoNegociacao';
type PedidoDaEmissao = ReturnType<ReturnType<typeof obterEmissaoAtualDoCertificado>>;

/** Uma rajada de eventos (mensagem e os status dela) vira uma consulta só. */
const ESPERA_PARA_AGRUPAR_ACONTECIMENTOS_MS = 2_000;

/**
 * Marco sem mensagem nem mudança de status e o resultado do porteiro não chegam pelo tempo real:
 * com a janela visível, a consulta aberta se reconfere neste intervalo. Bem abaixo do teto do core
 * (30 consultas por minuto por pessoa e caso).
 */
const INTERVALO_DA_RECONSULTA_MS = 120_000;

/**
 * A emissão vigente do Certificado da Negociação. Cada montagem (abrir a aba ou a página) pede a
 * emissão atual: o core devolve a mesma versão se nada mudou e uma nova se mudou (decisão
 * provisória 4). O PDF é sempre o da emissão exibida, e o download em voo é abortado ao sair,
 * para o arquivo de quem já saiu não ir para o disco.
 *
 * Com a consulta aberta, cada acontecimento do caso que chega ao store pede a emissão de novo
 * (ACC-01), agrupado em rajadas; a volta da janela ao primeiro plano e o relógio de 120 s (só com a
 * janela visível) também. Durante o download do PDF e durante uma abertura em curso, a
 * atualização espera: o PDF sai da emissão capturada, e só depois a tela passa para a nova.
 */
export function useCertificadoNegociacao(casoId, t0) {
  const $ = _c(63);
  let t1;
  if ($[0] !== t0) {
    t1 = t0 === undefined ? {} : t0;
    $[0] = t0;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const {
    baixarAoAbrir: t2
  } = t1;
  const baixarAoAbrir = t2 === undefined ? false : t2;
  const dispatch = useAppDispatch();
  const router = useRouter();
  const {
    showError
  } = useToast();
  let t3;
  if ($[2] !== casoId) {
    t3 = state => selectEstadoDoCertificadoNegociacao(state, casoId);
    $[2] = casoId;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const estado = useAppSelector(t3);
  const baixandoPdf = useAppSelector(selectCertificadoNegociacaoBaixandoPdf);
  const acontecimentos = useAppSelector(selectAcontecimentosDoCertificado);
  const {
    baixar,
    abortar
  } = useDownloadEmVoo(solicitarPdfDoCertificado.rejected.match);
  const ultimoPedido = useRef(null);
  const baixouAoAbrir = useRef(false);
  const acontecimentosAtendidos = useRef(acontecimentos);
  const filaPedida = useRef(null);
  const abrindo = estado.fase === "carregando";
  const numerosRegistrados = estado.fase === "pronto" ? estado.certificado.contagens.numerosRegistrados : null;
  let t4;
  if ($[4] !== baixar || $[5] !== casoId || $[6] !== dispatch || $[7] !== showError) {
    t4 = async certificado => {
      const desfecho = await baixar(() => dispatch(solicitarPdfDoCertificado({
        casoId,
        emissaoId: certificado.emissao.id,
        nomeArquivo: nomeDoArquivoDoCertificado(certificado)
      })));
      if (desfecho.tipo !== "falhou") {
        return;
      }
      showError("N\xE3o foi poss\xEDvel gerar o PDF", "Tente novamente em alguns instantes.");
    };
    $[4] = baixar;
    $[5] = casoId;
    $[6] = dispatch;
    $[7] = showError;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const baixarDaEmissao = t4;
  let t5;
  if ($[9] !== baixarAoAbrir || $[10] !== baixarDaEmissao || $[11] !== casoId || $[12] !== dispatch || $[13] !== router) {
    t5 = async () => {
      const pedido = dispatch(obterEmissaoAtualDoCertificado(casoId));
      ultimoPedido.current = pedido;
      const resultado = await pedido;
      if (ultimoPedido.current !== pedido) {
        return;
      }
      if (!obterEmissaoAtualDoCertificado.fulfilled.match(resultado)) {
        return;
      }
      if (!baixarAoAbrir || baixouAoAbrir.current) {
        return;
      }
      baixouAoAbrir.current = true;
      const download = baixarDaEmissao(resultado.payload);
      router.replace(rotaDoCertificado(casoId), {
        scroll: false
      });
      await download;
    };
    $[9] = baixarAoAbrir;
    $[10] = baixarDaEmissao;
    $[11] = casoId;
    $[12] = dispatch;
    $[13] = router;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const carregarEmissao = t5;
  let t6;
  if ($[15] !== acontecimentos || $[16] !== carregarEmissao) {
    t6 = () => {
      acontecimentosAtendidos.current = acontecimentos;
      carregarEmissao();
    };
    $[15] = acontecimentos;
    $[16] = carregarEmissao;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const carregarAoMontar = useEffectEvent(t6);
  let t7;
  if ($[18] !== abrindo || $[19] !== acontecimentos || $[20] !== baixandoPdf || $[21] !== casoId || $[22] !== dispatch) {
    t7 = () => {
      if (acontecimentosAtendidos.current === acontecimentos) {
        return;
      }
      if (baixandoPdf || abrindo) {
        return;
      }
      acontecimentosAtendidos.current = acontecimentos;
      dispatch(atualizarEmissaoDoCertificado(casoId));
    };
    $[18] = abrindo;
    $[19] = acontecimentos;
    $[20] = baixandoPdf;
    $[21] = casoId;
    $[22] = dispatch;
    $[23] = t7;
  } else {
    t7 = $[23];
  }
  const atualizarSeHouveAcontecimento = useEffectEvent(t7);
  let t8;
  if ($[24] !== casoId || $[25] !== dispatch) {
    t8 = () => {
      dispatch(reconsultaDoCertificadoPedida(casoId));
    };
    $[24] = casoId;
    $[25] = dispatch;
    $[26] = t8;
  } else {
    t8 = $[26];
  }
  const pedirReconsulta = useEffectEvent(t8);
  let t9;
  if ($[27] !== abortar || $[28] !== carregarAoMontar) {
    t9 = () => {
      carregarAoMontar();
      return () => {
        ultimoPedido.current = null;
        abortar();
      };
    };
    $[27] = abortar;
    $[28] = carregarAoMontar;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  let t10;
  if ($[30] !== abortar || $[31] !== casoId) {
    t10 = [abortar, casoId];
    $[30] = abortar;
    $[31] = casoId;
    $[32] = t10;
  } else {
    t10 = $[32];
  }
  useEffect(t9, t10);
  let t11;
  let t12;
  if ($[33] !== casoId || $[34] !== dispatch || $[35] !== numerosRegistrados) {
    t11 = () => {
      if (numerosRegistrados === null) {
        return;
      }
      const fila = `${casoId}:${numerosRegistrados}`;
      if (filaPedida.current === fila) {
        return;
      }
      filaPedida.current = fila;
      dispatch(fetchNegociacaoTelefones(casoId));
    };
    t12 = [casoId, dispatch, numerosRegistrados];
    $[33] = casoId;
    $[34] = dispatch;
    $[35] = numerosRegistrados;
    $[36] = t11;
    $[37] = t12;
  } else {
    t11 = $[36];
    t12 = $[37];
  }
  useEffect(t11, t12);
  let t13;
  if ($[38] !== atualizarSeHouveAcontecimento) {
    t13 = () => {
      const espera = setTimeout(atualizarSeHouveAcontecimento, ESPERA_PARA_AGRUPAR_ACONTECIMENTOS_MS);
      return () => clearTimeout(espera);
    };
    $[38] = atualizarSeHouveAcontecimento;
    $[39] = t13;
  } else {
    t13 = $[39];
  }
  let t14;
  if ($[40] !== acontecimentos) {
    t14 = [acontecimentos];
    $[40] = acontecimentos;
    $[41] = t14;
  } else {
    t14 = $[41];
  }
  useEffect(t13, t14);
  let t15;
  if ($[42] !== abrindo || $[43] !== atualizarSeHouveAcontecimento || $[44] !== baixandoPdf) {
    t15 = () => {
      if (baixandoPdf || abrindo) {
        return;
      }
      atualizarSeHouveAcontecimento();
    };
    $[42] = abrindo;
    $[43] = atualizarSeHouveAcontecimento;
    $[44] = baixandoPdf;
    $[45] = t15;
  } else {
    t15 = $[45];
  }
  let t16;
  if ($[46] !== abrindo || $[47] !== baixandoPdf) {
    t16 = [baixandoPdf, abrindo];
    $[46] = abrindo;
    $[47] = baixandoPdf;
    $[48] = t16;
  } else {
    t16 = $[48];
  }
  useEffect(t15, t16);
  let t17;
  if ($[49] !== pedirReconsulta) {
    t17 = () => {
      let relogio = null;
      const pararRelogio = () => {
        if (relogio !== null) {
          clearInterval(relogio);
        }
        relogio = null;
      };
      const ligarRelogio = () => {
        pararRelogio();
        relogio = setInterval(() => pedirReconsulta(), INTERVALO_DA_RECONSULTA_MS);
      };
      const aoMudarVisibilidade = () => {
        if (document.visibilityState !== "visible") {
          pararRelogio();
          return;
        }
        pedirReconsulta();
        ligarRelogio();
      };
      if (document.visibilityState === "visible") {
        ligarRelogio();
      }
      document.addEventListener("visibilitychange", aoMudarVisibilidade);
      return () => {
        document.removeEventListener("visibilitychange", aoMudarVisibilidade);
        pararRelogio();
      };
    };
    $[49] = pedirReconsulta;
    $[50] = t17;
  } else {
    t17 = $[50];
  }
  let t18;
  if ($[51] === Symbol.for("react.memo_cache_sentinel")) {
    t18 = [];
    $[51] = t18;
  } else {
    t18 = $[51];
  }
  useEffect(t17, t18);
  let t19;
  if ($[52] !== carregarEmissao) {
    t19 = () => {
      carregarEmissao();
    };
    $[52] = carregarEmissao;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const tentarNovamente = t19;
  let t20;
  if ($[54] !== baixarDaEmissao || $[55] !== estado.certificado || $[56] !== estado.fase) {
    t20 = async () => {
      if (estado.fase !== "pronto") {
        return;
      }
      await baixarDaEmissao(estado.certificado);
    };
    $[54] = baixarDaEmissao;
    $[55] = estado.certificado;
    $[56] = estado.fase;
    $[57] = t20;
  } else {
    t20 = $[57];
  }
  const baixarPdf = t20;
  let t21;
  if ($[58] !== baixandoPdf || $[59] !== baixarPdf || $[60] !== estado || $[61] !== tentarNovamente) {
    t21 = {
      estado,
      baixandoPdf,
      tentarNovamente,
      baixarPdf
    };
    $[58] = baixandoPdf;
    $[59] = baixarPdf;
    $[60] = estado;
    $[61] = tentarNovamente;
    $[62] = t21;
  } else {
    t21 = $[62];
  }
  return t21;
}